import { useApolloClient, useSubscription } from '@apollo/client';
import { Button, Snackbar } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Context as authorizationContext } from 'authorization/Context';
import { onNotificationCreatedSubscription } from 'notification-config/Schema';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getPendingManualAudioReturnPath,
  getPendingManualAudioSessionIds,
  MANUAL_AUDIO_EVENTS,
  ManualAudioNotice,
  publishManualAudioNotice,
  publishSoapNoteReady,
  removePendingManualAudioSession,
  SoapNoteReadyStatus,
  subscribeToManualAudioNotices,
} from 'woundscribe/core/manualAudioNotices';
import {
  markExistingTranscriptsStale,
  preferTranscriptSession,
} from 'woundscribe/core/recordingStore';
import { transcriptReadyPath } from 'woundscribe/core/utils';
import { ManualAudioUploadStatusQuery } from 'woundscribe/Schema';

/** Slow fallback while uploads are pending, in case a push notification is missed. */
const RECOVERY_INTERVAL_MS = 60_000;

const TRANSCRIPT_EVENTS: string[] = [
  MANUAL_AUDIO_EVENTS.completed,
  MANUAL_AUDIO_EVENTS.failed,
];
const SUMMARY_EVENTS: string[] = [
  MANUAL_AUDIO_EVENTS.summary_completed,
  MANUAL_AUDIO_EVENTS.summary_failed,
];
const NOTIFY_EVENTS: string[] = [...TRANSCRIPT_EVENTS, ...SUMMARY_EVENTS];

type ParsedResource = {
  eventId: string;
  sessionId: string;
  status: 'completed' | 'failed';
};

type ParsedSummaryResource = {
  sessionId: string;
  status: SoapNoteReadyStatus;
};

/** Accepts only transcript-completion resources. Summary events are handled separately. */
function parseManualAudioResource(raw: unknown): ParsedResource | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const resource = raw as Record<string, unknown>;
  const events = Array.isArray(resource.events) ? resource.events : [];
  const isTranscript = events.some((event) =>
    TRANSCRIPT_EVENTS.includes(event as string)
  );
  if (!isTranscript) {
    return null;
  }
  if (
    typeof resource.eventId !== 'string' ||
    typeof resource.sessionId !== 'string' ||
    (resource.status !== 'completed' && resource.status !== 'failed')
  ) {
    return null;
  }
  return {
    eventId: resource.eventId,
    sessionId: resource.sessionId,
    status: resource.status,
  };
}

/** Accepts only SOAP note summary resources. */
function parseSummaryResource(raw: unknown): ParsedSummaryResource | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const resource = raw as Record<string, unknown>;
  const events = Array.isArray(resource.events) ? resource.events : [];
  const isSummary = events.some((event) =>
    SUMMARY_EVENTS.includes(event as string)
  );
  if (!isSummary || typeof resource.sessionId !== 'string') {
    return null;
  }
  const status: SoapNoteReadyStatus = events.includes(
    MANUAL_AUDIO_EVENTS.summary_completed
  )
    ? 'summary_completed'
    : 'summary_failed';
  return { sessionId: resource.sessionId, status };
}

/** Completed Recording lives on the encounter the audio was uploaded from. */
const TRANSCRIPT_READY_MESSAGE = 'Your audio transcript is ready.';
const SHOW_TRANSCRIPT_LABEL = 'Show transcript';

/**
 * Mounted once in the private layout so uploads keep reporting when the user
 * navigates away from the upload screen.
 */
const ManualAudioNotifications: React.FC = () => {
  const { user } = useContext(authorizationContext);
  const navigate = useNavigate();
  const location = useLocation();
  const client = useApolloClient();
  const seenEventIds = useRef<Set<string>>(new Set());
  const [notice, setNotice] = useState<ManualAudioNotice | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(
    () =>
      subscribeToManualAudioNotices((next) => {
        setNotice(next);
        setOpen(true);
      }),
    []
  );

  /**
   * Shared by the push path and the recovery check. The key matches the eventId
   * wound-scribe sends, so the same outcome is never shown twice.
   */
  const handleOutcome = useCallback(
    (sessionId: string, status: ParsedResource['status']) => {
      const key = `${sessionId}:${status}`;
      if (seenEventIds.current.has(key)) {
        return;
      }
      seenEventIds.current.add(key);
      const returnPath = getPendingManualAudioReturnPath(sessionId);
      removePendingManualAudioSession(sessionId);

      if (status === 'completed') {
        // The transcript now exists on the encounter, so the one-transcript rule must see it
        // even if the user never opens it. Otherwise Start Recording stays enabled.
        markExistingTranscriptsStale();
        publishManualAudioNotice({
          severity: 'success',
          message: TRANSCRIPT_READY_MESSAGE,
          sessionId,
          returnPath,
        });
        return;
      }
      publishManualAudioNotice({
        severity: 'error',
        message: 'Transcription failed. Please try again.',
        sessionId,
      });
    },
    []
  );

  useSubscription(onNotificationCreatedSubscription, {
    variables: { id: user?.id, notify: NOTIFY_EVENTS },
    skip: !user?.id,
    onData: ({ data }) => {
      const raw = data.data?.onNotificationAdded?.resource;
      const transcriptResource = parseManualAudioResource(raw);
      if (transcriptResource) {
        handleOutcome(transcriptResource.sessionId, transcriptResource.status);
      }
      const summaryResource = parseSummaryResource(raw);
      if (summaryResource) {
        publishSoapNoteReady(summaryResource.sessionId, summaryResource.status);
      }
    },
  });

  /** Re-checks uploads still pending, e.g. after a reload, reconnect, or missed push. */
  const recoverPending = useCallback(async () => {
    const sessionIds = getPendingManualAudioSessionIds();
    await Promise.all(
      sessionIds.map(async (sessionId) => {
        try {
          const result = await client.query({
            query: ManualAudioUploadStatusQuery,
            variables: { sessionId },
            fetchPolicy: 'network-only',
          });
          const status = result.data?.manualAudioUploadStatus;
          if (status?.processingStatus === 'COMPLETED') {
            handleOutcome(sessionId, 'completed');
          } else if (
            status?.processingStatus === 'FAILED' ||
            status?.processingStatus === 'ABORTED' ||
            status?.recordingStatus === 'FAILED'
          ) {
            handleOutcome(sessionId, 'failed');
          }
        } catch {
          // Keep the session pending. The next check or the 24h expiry handles it.
        }
      })
    );
  }, [client, handleOutcome]);

  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }
    recoverPending();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        recoverPending();
      }
    };
    globalThis.addEventListener('focus', recoverPending);
    document.addEventListener('visibilitychange', onVisible);
    const timer = globalThis.setInterval(() => {
      if (getPendingManualAudioSessionIds().length > 0) {
        recoverPending();
      }
    }, RECOVERY_INTERVAL_MS);
    return () => {
      globalThis.removeEventListener('focus', recoverPending);
      document.removeEventListener('visibilitychange', onVisible);
      globalThis.clearInterval(timer);
    };
  }, [user?.id, recoverPending]);

  const handleView = () => {
    const path = transcriptReadyPath(notice?.returnPath, location.pathname);
    if (!notice?.sessionId || !path) {
      return;
    }
    preferTranscriptSession(notice.sessionId);
    setOpen(false);
    navigate(path);
  };

  const isReadyToView = Boolean(
    notice?.severity === 'success' &&
      notice.sessionId &&
      transcriptReadyPath(notice.returnPath, location.pathname)
  );

  return (
    <Snackbar
      open={open}
      autoHideDuration={isReadyToView ? null : 8000}
      onClose={() => setOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={notice?.severity ?? 'info'}
        onClose={() => setOpen(false)}
        action={
          isReadyToView ? (
            <Button color="inherit" size="small" onClick={handleView}>
              {SHOW_TRANSCRIPT_LABEL}
            </Button>
          ) : undefined
        }
      >
        {notice?.message}
      </Alert>
    </Snackbar>
  );
};

export default ManualAudioNotifications;
